import React, { useEffect, useRef } from 'react';
import 'ol/ol.css';
import './ol-extra.css';

import { DragBox, Select } from 'ol/interaction';
import { click, shiftKeyOnly } from 'ol/events/condition';

import {
    zoomToFloorplanExtent,
    reverseScale,
    createButtonControl,
    createMapWithControls,
    zoomChange,
    featureFromItem,
    createVectorLayerAndSource,
} from './mapFunctions.js';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// Start a bit more zoomed in than a plain fit-to-extent.
const INITIAL_ZOOM_OFFSET = 0.5;

function SvgViewer({
    elements,
    colorMap,
    onClickElement,
    inCartSeatIds,
    children,
    onBoxSelect,
    height = 600,
    mode = 'regular',
}) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const vectorSourceRef = useRef(null);
    const featureMapRef = useRef([]);
    const firstTimeElementsSetRef = useRef(false);
    const [popupPosition, setPopupPosition] = useState(null); // {x, y} in screen coordinates
    const [showLabels, setShowLabels] = useState(false);
    const resizeObserverRef = useRef(null);
    const { t, i18n } = useTranslation();

    const lang = i18n.language;

    const hasChildrenAsPopup = React.Children.count(children) > 0;

    useEffect(() => {
        if (!mapRef.current) return;

        const { vectorSource, vectorLayer } = createVectorLayerAndSource();
        vectorSourceRef.current = vectorSource;

        const fitControl = createButtonControl({
            label: '&#x2922;',
            title: 'Fit to Floorplan',
            className: 'fireknife-booking-selector__btn fireknife-btn fireknife-btn--default',
            onClick: () => zoomToFloorplanExtent(map, vectorSource, undefined, INITIAL_ZOOM_OFFSET),
        });
        const zoomInControl = createButtonControl({
            label: '+',
            title: 'Zoom In',
            className: 'fireknife-booking-selector__btn fireknife-btn fireknife-btn--default',
            onClick: () => zoomChange(mapInstanceRef.current.getView(), 0.5),
        });
        const zoomOutControl = createButtonControl({
            label: '-',
            title: t('svgeditor.zoomOut'),
            className: 'fireknife-booking-selector__btn fireknife-btn fireknife-btn--default',
            onClick: () => zoomChange(mapInstanceRef.current.getView(), -0.5),
        });

        const labelToggleControl = createButtonControl({
            label: '&#9872;',
            title: 'Toggle Labels',
            className: 'fireknife-booking-selector__btn fireknife-btn fireknife-btn--default',
            onClick: () => setShowLabels((prev) => !prev),
        });

        let oldPos = {
            center: [400, 300],
            zoom: 17,
        };
        if (mapInstanceRef.current) {
            oldPos = {
                center: mapInstanceRef.current.getView().getCenter(),
                zoom: mapInstanceRef.current.getView().getZoom(),
            };
        }

        const map = createMapWithControls(
            mapRef,
            vectorLayer,
            [fitControl, zoomInControl, zoomOutControl, labelToggleControl],
            {
                center: oldPos.center,
                zoom: oldPos.zoom,
                minZoom: 15, // Set minZoom in the view options
                maxZoom: 20, // Set maxZoom in the view options
            },
        );

        // Select interaction
        const selectInteraction = new Select({
            condition: click,
            layers: [vectorLayer],
            hitTolerance: 30,
            multi: false,
            style: null,
        });

        selectInteraction.on('select', (event) => {
            if (event.selected.length > 0) {
                const feature = event.selected[0];
                const id = feature.getId();
                if (id && typeof onClickElement === 'function') {
                    onClickElement(id);
                }
                if (hasChildrenAsPopup) {
                    const coordinates = feature.getGeometry().flatCoordinates;
                    // Convert map coordinates to screen pixel coordinates
                    const pixel = map.getPixelFromCoordinate(coordinates);
                    const mapElement = map.getTargetElement();
                    const mapRect = mapElement.getBoundingClientRect();

                    setPopupPosition({
                        x: mapRect.left + pixel[0],
                        y: mapRect.top + pixel[1] - 10, // 10px offset above the point
                    });
                }
            } else {
                setPopupPosition(null);
            }
        });

        // Update icon scales dynamically when zoom changes
        map.getView().on('change:resolution', () => {
            const zoom = map.getView().getZoom();
            featureMapRef.current.forEach((feature) => {
                const currentStyle = feature.getStyle();
                const image = currentStyle.getImage();
                if (image) {
                    image.setScale(reverseScale(zoom, feature.originalItem.scale));
                    feature.setStyle(currentStyle);
                }
            });
        });

        map.addInteraction(selectInteraction);

        if (typeof onBoxSelect === 'function') {
            const dragBox = new DragBox({
                condition: shiftKeyOnly,
                className: 'ol-dragbox-select',
            });
            dragBox.on('boxend', () => {
                const extent = dragBox.getGeometry().getExtent();
                const ids = [];
                vectorSource.forEachFeatureInExtent(extent, (feature) => {
                    const id = feature.getId();
                    if (id) ids.push(id);
                });
                if (ids.length > 0) onBoxSelect(ids);
            });
            map.addInteraction(dragBox);
        }

        mapInstanceRef.current = map;

        map.setTarget(mapRef.current);

        // Set up ResizeObserver to handle container resize
        if (mapRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.updateSize();
                }
            });
            resizeObserverRef.current.observe(mapRef.current);
        }

        return () => {
            map.setTarget(undefined);
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [onClickElement, onBoxSelect, t, hasChildrenAsPopup, setShowLabels]);

    useEffect(() => {
        if (!mapInstanceRef.current || !vectorSourceRef.current) return;
        setTimeout(() => {
            zoomToFloorplanExtent(mapInstanceRef.current, vectorSourceRef.current);
        }, 50);
    }, [mode]);

    useEffect(() => {
        if (!mapInstanceRef.current || !vectorSourceRef.current) return;

        featureMapRef.current = [];
        vectorSourceRef.current.clear();

        const zoom = mapInstanceRef.current.getView().getZoom();

        elements.forEach((item) => {
            //console.log('Adding feature for item:', item, lang);
            const color = colorMap[item.id] || 'white';
            const f = featureFromItem(
                item,
                lang,
                zoom,
                onClickElement,
                {
                    color,
                    opacity:
                        color === 'transparent' ? 0.2 : inCartSeatIds.includes(item.id) ? 0.5 : 1,
                },
                !showLabels, // remove labels flag
            );
            vectorSourceRef.current.addFeature(f);
            featureMapRef.current.push(f);
        });

        // if it's the first time, zoom to the extent of the features
        if (mapInstanceRef.current && elements.length > 0 && !firstTimeElementsSetRef.current) {
            firstTimeElementsSetRef.current = true;
            setTimeout(() => {
                zoomToFloorplanExtent(
                    mapInstanceRef.current,
                    vectorSourceRef.current,
                    undefined,
                    INITIAL_ZOOM_OFFSET,
                );
            }, 200);
        }
    }, [elements, colorMap, onClickElement, inCartSeatIds, lang, showLabels]);

    // Calculate constrained popup position
    const getConstrainedPosition = () => {
        if (!popupPosition) return null;

        const padding = 20;
        const popupWidth = 400; // Match maxWidth from Card component
        const popupHeight = 500; // Approximate height for compact view

        let x = popupPosition.x;
        let y = popupPosition.y;

        // Start with simple positioning - center horizontally, position above the point
        let finalX = x;
        let finalY = y - 20; // Small offset above the point
        let transform = 'translate(-50%, -100%)'; // Center horizontally, position above

        // Constrain horizontally
        const halfWidth = popupWidth / 2;
        if (finalX - halfWidth < padding) {
            // Too far left - align to left edge
            finalX = padding;
            transform = 'translate(0, -100%)'; // Left align
        } else if (finalX + halfWidth > window.innerWidth - padding) {
            // Too far right - align to right edge
            finalX = window.innerWidth - padding;
            transform = 'translate(-100%, -100%)'; // Right align
        }

        // Constrain vertically
        // Check if popup would go off the top edge
        if (y - popupHeight - 20 < padding) {
            // Position below the point instead
            finalY = y + 20;
            // Update transform to position below
            if (transform.includes('translate(0,')) {
                transform = 'translate(0, 0)'; // Left align, below
            } else if (transform.includes('translate(-100%,')) {
                transform = 'translate(-100%, 0)'; // Right align, below
            } else {
                transform = 'translate(-50%, 0)'; // Center, below
            }
        }

        // Final safety check - ensure popup is visible
        return { x: finalX, y: finalY, transform };
    };

    const constrainedPosition = getConstrainedPosition();

    return (
        <div style={{ position: 'relative', width: '100%', maxWidth: 800, margin: '0 auto' }}>
            <div ref={mapRef} style={{ width: '100%', height, textAlign: 'left' }}></div>
            {hasChildrenAsPopup && constrainedPosition && (
                <div
                    style={{
                        position: 'fixed',
                        left: `${constrainedPosition.x}px`,
                        top: `${constrainedPosition.y}px`,
                        transform: constrainedPosition.transform,
                        pointerEvents: 'auto',
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export default SvgViewer;
